import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useGET_learningStyleQuestionsQuery } from "redux/api/studentsApi"
import { STATUS_COLORS } from "utils/customizedTheme"
import { useGET_kpiEvaluationsQuery } from "redux/api/kpiEvaluationsApi"
import { OnlinePrediction, } from "@mui/icons-material"
import KpiAdjustmentsChart from "./KpAdjustmentsChart"
import { useGET_setupOptionsQuery } from "redux/api/setupApi"


interface IProps {
  subject: any
  student: string
  studentData: any
  kpiData: any
}

const KpiStudentDiagnostic: FC<IProps> = ({subject, student, kpiData, studentData }) => {
  const { t } = useTranslation()
  
  const { data: setupData } = useGET_setupOptionsQuery()
  
  const {
    data: learningStyle,
  } = useGET_learningStyleQuestionsQuery(student, {skip: !student})
    
  const learningCategories = learningStyle?.categories?.map((category) => {
    return {
      name: category.title,
      value: category.percentage,
      fill: category.color_hex_code,
    }
  })

  if(!studentData || !kpiData || !setupData) return <></>

  return (
    <>
        <div className="info-box">
            <div className="info-box-title" style={{ textAlign: 'left' }}>
                <div className="details-row">
                   {t('common.zpd-tag')}
                </div>
            </div>

            {setupData.filter(sd => sd.slug == "kpi_tracker_zpd_status" && sd.is_enabled).length == 1 && 
            <div className="info-box-content">
                <div className="stats-bar">
                    <div className="stats-bar-fill" style={{ 
                         width: `${kpiData.avg_accuracy_rate}%`,
                         background: STATUS_COLORS[kpiData.avg_accuracy_rate]
                    }}></div>

                    <div className="stats-bar-line" style={{ left: '50%' }}></div>
                    <div className="stats-bar-line" style={{ left: '80%' }}></div>
                </div>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: '5px' }}>
                    <div style={{ width: '50% '}}>{t('zpd-tags.gap')}</div>
                    <div style={{ width: '30%' }}>{t('zpd-tags.bridge')}</div>
                    <div>{t('zpd-tags.strength')}</div>
                </div>
            </div>}

        </div>


                  
        <div className="info-row">

                    <div className="info-box">
                        <div className="info-box-title">{t('common.kpi-metrics')}</div>
                        <div className="info-box-content">
                            <div className="stats-rows">
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.mastery')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${studentData.baseline_score}%`,
                                            background: '#b82aceff'
                                    }}></div>
                                        <div className="stats-bar-value">{Math.round(Number(studentData.baseline_score) || 0)}%</div>
                                    </div>
                                </div>

                                {setupData.filter(sd => sd.slug == "kpi_tracker_accuracy_rate" && sd.is_enabled).length == 1 && 
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.accuracy-rate')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpiData.avg_accuracy_rate}%`,
                                            background: '#0d40d7ff'
                                    }}></div>
                                        <div className="stats-bar-value">{Math.round(Number(kpiData.avg_accuracy_rate) || 0)}%</div>
                                    </div>
                                </div>}

                                {setupData.filter(sd => sd.slug == "kpi_tracker_time_on_task" && sd.is_enabled).length == 1 && 
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.time-on-task')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpiData.avg_time_on_task}%`,
                                            background: 'orange'
                                    }}></div>
                                        <div className="stats-bar-value">{Math.round(Number(kpiData.avg_time_on_task) || 0)}%</div>
                                    </div>
                                </div>}

                                {setupData.filter(sd => sd.slug == "kpi_tracker_growth_trend" && sd.is_enabled).length == 1 && 
                                <div className="stats-row">
                                    <div className="stats-row-label">{t('common.growth-trend')}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${kpiData.avg_growth_trend > 0 ? kpiData.avg_growth_trend : (-1) * kpiData.avg_growth_trend}%`,
                                            background: kpiData.avg_growth_trend > 0 ? 'rgba(8, 218, 33, 1)' : 'red'
                                    }}></div>
                                        <div className="stats-bar-value">{Math.round(Number(kpiData.avg_growth_trend) || 0)}%</div>
                                    </div>
                                </div>}

                            </div>
                           
                        </div>
                    </div>

                    {learningCategories?.length > 0 && 
                    <div className="info-box">
                        <div className="info-box-title">{t('student.learning-style')}</div>
                        <div className="info-box-content">
                            <div className="stats-rows">
                                {learningCategories.map(c => <div key={c.name} className="stats-row">
                                    <div className="stats-row-label">{c.name}</div>
                                    <div className="stats-bar">
                                        <div className="stats-bar-fill" style={{ 
                                            width: `${c.value}%`,
                                            background: c.fill
                                        }}></div>
                                        <div className="stats-bar-value">{Math.round(Number(c.value) || 0)}%</div>
                                    </div>
                                </div>)}
                            </div>
                        </div>
                    </div>}
                </div>

                <div className="info-row">
                    <div className="info-box">
                        <div className="info-box-title">
                            {t('common.scaffold-suggestion')}
                            <span style={{ marginLeft: '8px', fontSize: '0.8em', fontWeight: 'normal', fontStyle: 'italic', opacity: 0.7 }}>
                                ({t('common.guideline-only')})
                            </span>
                        </div>
                        <div className="info-box-content">
                            {kpiData.scaffolds.map((s, idx) => <div key={idx}>&bull; {s}</div>)}
                        </div>
                    </div>
                    <div className="info-box">
                        <div className="info-box-title">{t('common.error-pattern')}</div>
                        <div className="info-box-content">
                            {(kpiData.error_patterns ?? []).map((e, idx) => <div key={idx}>&bull; {e}</div>)}
                        </div>
                    </div>
                </div>

                <div className="info-row">
                    <div className="info-box">
                        <div className="info-box-title">{t('common.scaffold-usage')}</div>
                        <div className="info-box-content">
                            <KpiAdjustmentsChart filters={{
                                student: student,
                            }}/>
                        </div>
                    </div>       
                </div>



    </>
  )
}

export default KpiStudentDiagnostic